import {
  VOYAGE_MODEL_DIMENSIONS,
  DEFAULT_VOYAGE_EMBEDDING_MODEL,
  DEFAULT_VOYAGE_RERANK_MODEL,
  type KnowledgeCollectionSchema,
  type KnowledgeFilterableField,
  type KnowledgeQdrantCollection,
} from "../types/knowledgeProviders.ts";

// The same Qdrant base URL rule the qdrant-collections function applies when it loads the
// credential, so the Providers form can refuse a URL the function would treat as unconfigured.
export { normalizeBaseUrl as normalizeQdrantBaseUrl } from "../../supabase/functions/qdrant-collections/logic.ts";

// Field types that become LLM tool parameters (contract: kb-contract.md). datetime and geo
// have no parameter mapping in the contract, so they are not offered as filters.
const FILTER_PARAM_TYPE: Record<string, "string" | "integer" | "number" | "boolean"> = {
  keyword: "string",
  text: "string",
  uuid: "string",
  integer: "integer",
  float: "number",
  bool: "boolean",
};

// Tool name for a collection: search_<slug>, [a-z0-9_], <= 64 chars, numeric suffix on collision.
export function slugToolName(collection: string, taken: string[] = []): string {
  const slug = collection.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "kb";
  const base = `search_${slug}`.slice(0, 60);
  if (!taken.includes(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
}

// Param name an LLM tool gets for a payload field: [A-Za-z0-9_] only.
export function paramName(field: string): string {
  return field.replace(/[^A-Za-z0-9_]/g, "_");
}

export function isFilterableType(type: string): boolean {
  return type in FILTER_PARAM_TYPE;
}

/** Vector size the collection expects for the chosen named vector (or the single vector). */
export function expectedVectorSize(
  schema: KnowledgeCollectionSchema,
  vectorName: string | null,
): number | null {
  if (vectorName) return schema.named_vectors?.[vectorName] ?? null;
  return schema.vector_size;
}

/** Whether the model can produce vectors of the collection's size; `reason` says why not. */
export function checkModelFitsCollection(
  model: string,
  size: number | null,
): { ok: boolean; reason?: string } {
  const sizes = VOYAGE_MODEL_DIMENSIONS[model as keyof typeof VOYAGE_MODEL_DIMENSIONS];
  if (!sizes) return { ok: false, reason: `Unknown embedding model ${model}.` };
  if (size === null) return { ok: true };
  if (!sizes.includes(size)) {
    return {
      ok: false,
      reason: `${model} cannot produce ${size}-dimension vectors. It supports ${[...sizes].sort((a, b) => a - b).join(", ")}.`,
    };
  }
  return { ok: true };
}

export interface ToolPreview {
  name: string;
  description: string;
  parameters: {
    type: "object";
    properties: Record<string, { type: string; description?: string }>;
    required: string[];
  };
}

// The tool the LLM receives for one collection. voxi-2-0 builds the real one from the same config.
export function buildToolPreview(
  c: Pick<KnowledgeQdrantCollection, "tool_name" | "description" | "filterable_fields">,
): ToolPreview {
  const properties: ToolPreview["parameters"]["properties"] = {
    query: { type: "string", description: "What to search for" },
  };
  const used = new Set<string>(["query"]);

  for (const field of c.filterable_fields) {
    const type = FILTER_PARAM_TYPE[field.type];
    const name = paramName(field.name);
    if (!type || used.has(name)) continue;
    const range = type === "integer" || type === "number";
    const names = range ? [name, `${name}_min`, `${name}_max`] : [name];
    if (names.some((n) => used.has(n))) continue;
    for (const n of names) {
      used.add(n);
      properties[n] = { type };
    }
  }

  return {
    name: c.tool_name,
    description: c.description,
    parameters: { type: "object", properties, required: ["query"] },
  };
}

/** Filterable fields = payload fields that have a Qdrant payload index and a parameter mapping. */
export function filterableFromSchema(schema: KnowledgeCollectionSchema): KnowledgeFilterableField[] {
  return schema.payload_fields
    .filter((f) => f.indexed && isFilterableType(f.type))
    .map((f) => ({ name: f.name, type: f.type as KnowledgeFilterableField["type"] }));
}

export function newCollectionConfig(
  collection: string,
  schema: KnowledgeCollectionSchema,
  description: string,
  takenToolNames: string[],
): KnowledgeQdrantCollection {
  const named = Object.keys(schema.named_vectors ?? {});
  return {
    id: crypto.randomUUID(),
    enabled: true,
    collection,
    vector_name: named.length ? named[0] : null,
    embedding_model: DEFAULT_VOYAGE_EMBEDDING_MODEL,
    tool_name: slugToolName(collection, takenToolNames),
    description,
    max_results: 5,
    score_threshold: null,
    rerank: false,
    rerank_model: DEFAULT_VOYAGE_RERANK_MODEL,
    returned_fields: [],
    filterable_fields: filterableFromSchema(schema),
    schema,
  };
}

/** Refresh a saved collection from a new schema, keeping the user's own settings. */
export function applyRefreshedSchema(
  c: KnowledgeQdrantCollection,
  schema: KnowledgeCollectionSchema,
): KnowledgeQdrantCollection {
  const names = new Set(schema.payload_fields.map((f) => f.name));
  const named = Object.keys(schema.named_vectors ?? {});
  return {
    ...c,
    schema,
    vector_name: named.length ? (c.vector_name && named.includes(c.vector_name) ? c.vector_name : named[0]) : null,
    returned_fields: c.returned_fields.filter((f) => names.has(f)),
    filterable_fields: filterableFromSchema(schema),
  };
}
